import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getSuperSectors, createSuperSector, updateSuperSector, deleteSuperSector
} from "../../../redux/actions/profile";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";

export default function AdminSectors() {
  const dispatch = useDispatch();
  const sectors = useSelector((s) => s.profile.superSectors || []);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", locations: [] });
  const [locInput, setLocInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => { dispatch(getSuperSectors()); }, [dispatch]);

  const resetForm = () => {
    setForm({ name: "", description: "", locations: [] });
    setLocInput("");
    setEditing(null);
    setShowForm(false);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, description: s.description || "", locations: [...(s.locations || [])] });
    setLocInput("");
    setShowForm(true);
  };

  const addLocation = () => {
    const loc = locInput.trim();
    if (loc && !form.locations.includes(loc)) {
      setForm((f) => ({ ...f, locations: [...f.locations, loc] }));
    }
    setLocInput("");
  };

  const removeLocation = (loc) => {
    setForm((f) => ({ ...f, locations: f.locations.filter((l) => l !== loc) }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    const payload = { name: form.name.trim(), description: form.description, locations: form.locations };
    let result;
    if (editing) {
      result = await dispatch(updateSuperSector(editing._id, payload));
    } else {
      result = await dispatch(createSuperSector(payload));
    }
    setSaving(false);
    if (result.success) {
      dispatch(getSuperSectors());
      resetForm();
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this sector? Agents assigned to it will be unassigned.")) return;
    setDeleting(id);
    await dispatch(deleteSuperSector(id));
    setDeleting(null);
    dispatch(getSuperSectors());
  };

  const firstMap = (s) => {
    const loc = s.locations && s.locations[0];
    return loc ? `https://maps.google.com/maps?q=${encodeURIComponent(loc)}&output=embed` : null;
  };

  return (
    <div style={S.root}>
      <div style={S.header}>
        <div>
          <h2 style={S.title}>Sectors</h2>
          <p style={S.sub}>Create and manage geographic sectors for agents</p>
        </div>
        <button style={S.btnPrimary} onClick={() => { resetForm(); setShowForm(true); }}>
          <i className="bi bi-plus-circle-fill" style={{ marginRight: 6 }} />New Sector
        </button>
      </div>

      {showForm && (
        <div style={S.formCard}>
          <h3 style={S.formTitle}>{editing ? "Edit Sector" : "Create Sector"}</h3>
          <div style={S.formGrid}>
            <div style={S.fieldGroup}>
              <label style={S.label}>Sector Name *</label>
              <input style={S.input} placeholder="e.g. Lagos Island" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div style={S.fieldGroup}>
              <label style={S.label}>Description</label>
              <input style={S.input} placeholder="Brief description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={S.label}>Locations / Areas</label>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <input
                style={{ ...S.input, flex: 1 }}
                placeholder="e.g. Victoria Island, Lagos"
                value={locInput}
                onChange={(e) => setLocInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addLocation()}
              />
              <button style={S.btnAdd} onClick={addLocation}>
                <i className="bi bi-plus-lg" /> Add
              </button>
            </div>
            {form.locations.length > 0 && (
              <div style={S.locationList}>
                {form.locations.map((loc) => (
                  <div key={loc} style={S.locationChip}>
                    <i className="bi bi-geo-alt-fill" style={{ color: PRIMARY, fontSize: 11, marginRight: 4 }} />
                    {loc}
                    <button style={S.chipRemove} onClick={() => removeLocation(loc)}><i className="bi bi-x" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button style={S.btnPrimary} onClick={handleSave} disabled={saving || !form.name.trim()}>
              {saving ? "Saving…" : editing ? "Update Sector" : "Create Sector"}
            </button>
            <button style={S.btnCancel} onClick={resetForm}>Cancel</button>
          </div>
        </div>
      )}

      {sectors.length === 0 ? (
        <div style={S.empty}>
          <i className="bi bi-map" style={{ fontSize: 40, color: "#ccc" }} />
          <p style={{ color: "#aaa", marginTop: 12 }}>No sectors created yet. Create one to start assigning agents.</p>
        </div>
      ) : (
        <div style={S.grid}>
          {sectors.map((s) => {
            const mapSrc = firstMap(s);
            return (
              <div key={s._id} style={S.card}>
                {mapSrc && (
                  <iframe title={s.name} src={mapSrc} style={S.cardMap} frameBorder="0" allowFullScreen />
                )}
                {!mapSrc && (
                  <div style={S.noMap}>
                    <i className="bi bi-map" style={{ fontSize: 28, color: "#ddd" }} />
                    <span style={{ fontSize: 11, color: "#ccc", marginTop: 4 }}>No locations added</span>
                  </div>
                )}
                <div style={S.cardBody}>
                  <div style={S.cardName}>{s.name}</div>
                  {s.description && <div style={S.cardDesc}>{s.description}</div>}
                  {s.locations && s.locations.length > 0 && (
                    <div style={S.cardLocs}>
                      {s.locations.map((loc, i) => (
                        <span key={i} style={S.locTag}>
                          <i className="bi bi-geo-alt" style={{ fontSize: 10, marginRight: 3 }} />{loc}
                        </span>
                      ))}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: "#bbb", marginBottom: 12 }}>
                    Created {new Date(s.createdAt).toLocaleDateString()}
                  </div>
                  <div style={S.cardActions}>
                    <button style={S.editBtn} onClick={() => openEdit(s)}>
                      <i className="bi bi-pencil-fill" /> Edit
                    </button>
                    <button style={S.deleteBtn} disabled={deleting === s._id} onClick={() => handleDelete(s._id)}>
                      {deleting === s._id ? "…" : <><i className="bi bi-trash3-fill" /> Delete</>}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const S = {
  root: { paddingBottom: 40 },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 800, color: BG, margin: 0 },
  sub: { fontSize: 13, color: "#888", marginTop: 4 },
  btnPrimary: { background: PRIMARY, color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center" },
  btnAdd: { background: BG, color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" },
  btnCancel: { background: "#f5f5f5", color: "#888", border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 13, cursor: "pointer" },
  formCard: { background: "#fff", borderRadius: 10, padding: "24px", boxShadow: "0 2px 10px rgba(0,0,0,0.07)", marginBottom: 28 },
  formTitle: { fontSize: 15, fontWeight: 800, color: BG, margin: "0 0 20px" },
  formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 11, fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: 0.5 },
  input: { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" },
  locationList: { display: "flex", flexWrap: "wrap", gap: 8 },
  locationChip: { display: "flex", alignItems: "center", background: "rgba(167,204,72,0.1)", border: "1px solid rgba(167,204,72,0.3)", borderRadius: 20, padding: "4px 10px", fontSize: 12, color: "#4a6a00" },
  chipRemove: { background: "none", border: "none", cursor: "pointer", color: "#aaa", fontSize: 14, padding: "0 0 0 6px", lineHeight: 1 },
  empty: { textAlign: "center", padding: "60px 0" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 },
  card: { background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 10px rgba(0,0,0,0.07)" },
  cardMap: { width: "100%", height: 160, border: "none", display: "block" },
  noMap: { height: 120, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#f9f9f9" },
  cardBody: { padding: "14px 16px" },
  cardName: { fontSize: 15, fontWeight: 800, color: BG, marginBottom: 4 },
  cardDesc: { fontSize: 12, color: "#888", marginBottom: 8 },
  cardLocs: { display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 },
  locTag: { fontSize: 11, background: "#f5f5f5", color: "#666", borderRadius: 20, padding: "2px 8px", display: "flex", alignItems: "center" },
  cardActions: { display: "flex", gap: 8 },
  editBtn: { flex: 1, background: "rgba(44,45,63,0.07)", color: BG, border: "none", borderRadius: 7, padding: "7px 0", fontSize: 12, cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 5 },
  deleteBtn: { flex: 1, background: "rgba(230,57,70,0.08)", color: "#e63946", border: "none", borderRadius: 7, padding: "7px 0", fontSize: 12, cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 5 },
};
